import { IBaseStore } from '../types'
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import Api from '@/api'
import {
  EOrigin,
  EPaymentsDepositStatus,
  EPaymentStatus,
  ESortOrder,
  IAutoPayItem,
  IAutoPayItemFrontEndFilters,
  IAutoPaySetting, IFacilityPayerTypePaymentSetting,
  IGetPccDepositListDto,
  IPaymentsFiltersDTO,
  IPaymentStatus,
  IPaymentTransactionEffectiveDate,
  IPccDepositResponse,
  IResidentAutoPaySettingFilters,
  IResidentPaymentMethod,
  IScheduledPayment,
  IScheduleItemsFilters,
  ISchedulePaymentItem,
  ISchedulePaymentSetting,
  IScheduleSettingStatisticsDTO,
  ITransactionToPay,
  pccDepositPaymentDistribution
} from '@zunta/configurations'

export interface IPaymentsState {
  paymentStatusList: any;
  selectedAutoPayItem: IAutoPayItem | null;
  facilityPaymentsNotInSync: IPaymentStatus[];
  total: number;
  transactions: IPaymentTransactionEffectiveDate[];
  rejectTransactions: IPaymentTransactionEffectiveDate[];
  residentScheduledPayments: IScheduledPayment[];
  transactionsToApply: ITransactionToPay[];
  scheduleSettingStatistics: IScheduleSettingStatisticsDTO | null;
  paymentPayerTypes: IFacilityPayerTypePaymentSetting[];
}

export interface IPaymentsStore extends IBaseStore {
  state: IPaymentsStore
}
@Module({
  name: 'payments',
  namespaced: true
})
export class PaymentsModule extends VuexModule {
  paymentStatusList: any = [];
  total: number = 0;
  filters: any = {
    skip: 0,
    limit: 20
  };
  scheduleItems: ISchedulePaymentItem[] = [];
  currentAutoPaySetting: IAutoPaySetting | null = null;
  autoPayItems: IAutoPayItem[] = [];
  autoPaySettings: IAutoPaySetting[] = [];
  scheduleItemsTotal: number = 0;
  autoPayItemsTotal: number = 0;
  autoPaySettingsTotal: number = 0;
  transactions: IPaymentTransactionEffectiveDate[] = [];
  rejectTransactions: IPaymentTransactionEffectiveDate[] = [];
  residentScheduledPayments: IScheduledPayment[] = [];
  transactionsToApply: ITransactionToPay[] = [];
  scheduleSettingStatistics: IScheduleSettingStatisticsDTO | null = null;
  deposits: any = [];
  pccDeposits: IPccDepositResponse = { deposits: [], total: 0, depositPayerLines: 0 };
  pccDepositsDistribution: pccDepositPaymentDistribution[] = [];
  paymentsListLastQuery: any = {};
  depositsFilters: any = {
    skip: 0,
    limit: 20
  };
  selectedAutoPayItem: IAutoPayItem | null = null;
  facilitiesPaymentsNotInSync: any[] = [];
  openedDeposit: any = null;
  depositPayments: any = [];
  depositPaymentsTotal: number = 0;
  currentPayment: any = {};
  paymentPayerTypes: IFacilityPayerTypePaymentSetting[] = []

  @Mutation
  SET_PAYMENTS_LIST_LAST_QUERY (lastQuery: any) {
    this.paymentsListLastQuery = lastQuery
  }

  @Mutation
  SET_CURRENT_PAYMENT (payment: any) {
    this.currentPayment = payment
  }

  @Mutation
  SET_AUTO_PAY_ITEM (item: IAutoPayItem) {
    this.selectedAutoPayItem = item
  }

  @Mutation
  UPDATE_DEPOSIT_PAYMENTS_TOTAL (total: number) {
    this.depositPaymentsTotal = total
  }

  @Mutation
  UPDATE_DEPOSIT_PAYMENTS (payments: any[]) {
    this.depositPayments = payments
  }

  @Mutation
  SET_FACILITIES_PAYMENTS_NOT_IN_SYNC (payments: any) {
    this.facilitiesPaymentsNotInSync = payments
  }

  @Mutation
  UPDATE_DEPOSIT_STATUS (input: { depositId: number, status: EPaymentsDepositStatus }) {
    for (let i = 0; i < this.deposits.length; i++) {
      if (this.deposits[i].id === input.depositId) {
        if (this.deposits[i].entity) {
          this.deposits[i] = {
            ...this.deposits[i],
            entity: {
              ...this.deposits[i],
              status: input.status
            }
          }
        } else {
          this.deposits[i] = {
            ...this.deposits[i],
            entity: {
              willowDepositId: input.depositId,
              status: input.status
              }
          }
        }
      }
    }
  }

  @Mutation
  SET_DEPOSITS (deposits: any[]) {
    this.deposits = deposits
  }

  @Mutation
  SET_PCC_DEPOSITS (deposits: IPccDepositResponse) {
    this.pccDeposits = deposits
  }

  @Mutation
  RESET_PCC_DEPOSITS () {
    this.pccDeposits = { deposits: [], total: 0, depositPayerLines: 0 }
  }

  @Mutation
  SET_PCC_DEPOSITS_DISTRIBUTION (distribution: pccDepositPaymentDistribution[]) {
    this.pccDepositsDistribution = distribution
  }

  @Mutation
  SET_SCHEDULE_SETTING_STATISTICS (statistics: IScheduleSettingStatisticsDTO | null) {
    this.scheduleSettingStatistics = statistics
  }

  @Mutation
  UPDATE_SCHEDULE_SETTING_PAYMENT_METHOD (input: { scheduleSettingId: number, paymentMethod: IResidentPaymentMethod }) {
    this.autoPayItems.forEach((autoPayItem: IAutoPayItem) => {
      const setting = autoPayItem.setting as IAutoPaySetting
      if (setting.id === input.scheduleSettingId) {
        setting.paymentMethod = input.paymentMethod
      }
    })
  }

  @Mutation
  DELETE_SCHEDULE_ITEM (scheduleItemId: number | string) {
    this.scheduleItems = this.scheduleItems.filter((item: ISchedulePaymentItem) => (
      item.id.toString() !== scheduleItemId.toString()
    ))
    this.scheduleItemsTotal--
  }

  @Mutation
  SET_TRANSACTIONS_TO_APPLY (transactionsToApply: ITransactionToPay[]) {
    this.transactionsToApply = transactionsToApply
  }

  @Mutation
  SET_SCHEDULE_ITEMS_LIST (scheduleItems: ISchedulePaymentItem[]) {
    this.scheduleItems = scheduleItems
  }

  @Mutation
  SET_SCHEDULE_TOTAL (total: number) {
    this.scheduleItemsTotal = total
  }

  @Mutation
  SET_AUTOPAY_ITEMS (autoPayItems: IAutoPayItem[]) {
    this.autoPayItems = autoPayItems
  }

  @Mutation
  SET_AUTOPAY_ITEMS_TOTAL (total: number) {
    this.autoPayItemsTotal = total
  }

  @Mutation
  SET_AUTOPAY_SETTINGS (autoPaySettings: IAutoPaySetting[]) {
    this.autoPaySettings = autoPaySettings
  }

  @Mutation
  SET_AUTOPAY_SETTINGS_TOTAL (total: number) {
    this.autoPaySettingsTotal = total
  }

  @Mutation
  SET_CURRENT_AUTOPAY_SETTING (autoPaySetting: IAutoPaySetting | null) {
    this.currentAutoPaySetting = autoPaySetting
  }

  @Mutation
  UPDATE_RESIDENT_SCHDULED_PAYMENTS (scheduledPayments: IScheduledPayment[]) {
    this.residentScheduledPayments = scheduledPayments
  }

  @Mutation
  UPDATE_PAYMENT_TRANSACTIONS (transactions: IPaymentTransactionEffectiveDate[]) {
    this.transactions = transactions
  }

  @Mutation
  UPDATE_REJECT_TRANSACTIONS (rejectTransactions: IPaymentTransactionEffectiveDate[]) {
    this.rejectTransactions = rejectTransactions
  }

  @Mutation
  UPDATE_PAYMENT_TOTAL (total: number) {
    this.total = total
  }

  @Mutation
  UPDATE_PAYMENT_LIST (data: any) {
    this.paymentStatusList = data
  }

  @Mutation
  SET_FILTERS (filters: IScheduleItemsFilters) {
    this.filters = filters
  }

  @Mutation
  DELETE_FILTER (field: keyof any) {
    delete this.filters[field]
  }

  @Mutation
  SET_PAYMENT_PAYER_TYPES (payerTypes: any) {
    this.paymentPayerTypes = payerTypes
  }

  @Action
  async getPaymentStatusById (id: any) {
    const res = await Api.payments.getPaymentStatusById(id)
    this.SET_CURRENT_PAYMENT(res)
  }

  @Action
  setFilters (filters: any) {
    this.SET_FILTERS(filters)
  }

  @Action
  deleteFilter (field: string) {
    this.DELETE_FILTER(field as keyof any)
  }

  @Action
  async getResidentSchduledPayments (residentId: number | string) {
    const schduledPayments = await Api.payments.getResidentSchduledPayments(residentId)
    this.UPDATE_RESIDENT_SCHDULED_PAYMENTS(schduledPayments)
  }

  @Action
  async getPaymentTransactions (paymentId: number | string) {
    const transactions = await Api.payments.getResidentPaymentTransactions(paymentId)
    this.UPDATE_PAYMENT_TRANSACTIONS(transactions)
  }

  @Action
  async getRejectTransactions (paymentId: number | string) {
    const rejectTransactions = await Api.payments.getResidentRejectTransactions(paymentId)
    this.UPDATE_REJECT_TRANSACTIONS(rejectTransactions)
  }

  @Action
  async getScheduledPaymentTransactions (paymentId: number | string) {
    const transactions = await Api.payments.getScheduledPaymentTransactions(paymentId)
    // console.log(transactions)
    this.UPDATE_PAYMENT_TRANSACTIONS(transactions)
  }

  @Action
  async getPaymentsList (filters:IPaymentsFiltersDTO) {
    if (filters.resident && !filters.sortBy && !filters.sortOrder) {
      filters.sortBy = 'paidDate'
      filters.sortOrder = ESortOrder.DESC
    }
    const { total, payments } = await Api.payments.getPaymentsList(filters)
    this.SET_PAYMENTS_LIST_LAST_QUERY(filters)
    this.UPDATE_PAYMENT_LIST(payments)
    this.UPDATE_PAYMENT_TOTAL(total)
  }

  // @Action
  // async downloadPaymentsListReport () {
  //   const query = JSON.parse(JSON.stringify(this.paymentsListLastQuery))
  //   delete query.limit
  //   delete query.skip
  //   await downloadFile('/api/payments/download-payments-history-report', query)
  // }

  @Action
  async getResidentPaymentsList (input: { residentId: string | number, filters: IPaymentsFiltersDTO }) {
    const { total, payments } = await Api.payments.getResidentPaymentsByFilters(input.residentId, input.filters)
    this.UPDATE_PAYMENT_LIST(payments)
    this.UPDATE_PAYMENT_TOTAL(total)
  }

  @Action
  async fetchAutoPayItem (autoPayItemId: number | string) {
    const autoPayItem = await Api.payments.getAutoPayItem(autoPayItemId)
    this.SET_AUTO_PAY_ITEM(autoPayItem)
  }

  @Action
  async sendPaymentReceiptViaEmail (input: { paymentId: number | string, contact: { name: string, email: string } }) {
    const response = await Api.payments.sendPaymentReceiptViaEmail(input.paymentId, input.contact)
    return response
  }

  @Action
  async sendPaymentReceiptViaMail (input: {
    paymentId: number | string,
    contact: {
      name: string,
      address: string
      addressLine1: string,
      addressLine2?: string,
      state: string,
      city: string,
      zip: string
    }
  }) {
    const response = await Api.payments.sendPaymentReceiptViaMail(input.paymentId, input.contact)
    return response
  }

  @Action({ rawError: true })
  async fetchScheduleItemsListByFilters (filters: IAutoPayItemFrontEndFilters) {
    const response = await Api.payments.getScheduleItemsByFilters(filters)
    this.SET_SCHEDULE_ITEMS_LIST(response ? response.items : [])
    this.SET_SCHEDULE_TOTAL(response ? response.total : 0)
  }

  @Action({ rawError: true })
  async fetchChainAutoPayItemsByFilters (filters: IAutoPayItemFrontEndFilters) {
    const response = await Api.payments.getChainAutoPayItemsByFilters(filters)
    this.SET_AUTOPAY_ITEMS(response ? response.items : [])
    this.SET_AUTOPAY_ITEMS_TOTAL(response ? response.total : 0)
  }

  @Action
  async fetchAutoPayItems (resident: number | string) {
    const response = await Api.payments.getAutoPayItems(resident)
    this.SET_AUTOPAY_ITEMS(response ? response.items : [])
    this.SET_AUTOPAY_ITEMS_TOTAL(response ? response.total : 0)
  }

  @Action
  async fetchResidentAutoPaySettings (payload: { resident: number | string, filters?: IResidentAutoPaySettingFilters }) {
    const { resident, filters } = payload
    const response = await Api.payments.getResidentAutoPaySettings(resident, filters)
    this.SET_AUTOPAY_SETTINGS(response ? response.items : [])
    this.SET_AUTOPAY_SETTINGS_TOTAL(response ? response.total : 0)
  }

  @Action
  async fetchAutoPaySetting (settingId: number) {
    const response = await Api.payments.getAutoPaySetting(settingId)
    await this.SET_CURRENT_AUTOPAY_SETTING(response)
  }

  @Action
  async resetCurrentAutoPaySetting () {
    await this.SET_CURRENT_AUTOPAY_SETTING(null)
  }

  @Action
  async getScheduleItemTransactionsForApply (scheduleItemId: number | string) {
    const transactions = await Api.payments.getScheduleItemTransactionsForApply(scheduleItemId)
    this.SET_TRANSACTIONS_TO_APPLY(transactions)
  }

  @Action
  async autoPayItemPostpone (input: { autoPayItemId: number | string, date: string | Date }) {
    const response = await Api.payments.autoPayItemPostpone(input.autoPayItemId, input.date)
    if (response.statusCode === 400) {
      return { status: 'Failed', message: [response.message] }
    }
    return response
  }

  @Action
  async autoPayItemSkip (autoPayItemId: number | string) {
    const response = await Api.payments.autoPayItemSkip(autoPayItemId)
    if (response === 'OK') {
      this.DELETE_SCHEDULE_ITEM(autoPayItemId)
    }
    if (response.statusCode === 400) {
      return { status: 'Failed', message: [response.message] }
    }
    return response
  }

  @Action
  async autoPayStop (input: { autoPayItemId: number | string, autoPaySettingId: string | number, stoppedFromReview: boolean }) {
    const response = await Api.payments.autoPayStop(input.autoPaySettingId, input.stoppedFromReview)
    if (response === 'OK') {
      this.DELETE_SCHEDULE_ITEM(input.autoPayItemId)
    }
    if (response.statusCode === 400) {
      return { status: 'Failed', message: [response.message] }
    }
    return response
  }

  @Action
  async autoPayDelete (autoPaySettingId: string | number) {
    await Api.payments.autoPayStop(autoPaySettingId, false)
  }

  @Action
  async autoPayItemProcess (autoPayItemId: number | string) {
    const response = await Api.payments.autoPayItemProcess(autoPayItemId)
    if (response.statusCode === 400) {
      return { status: 'Failed', message: [response.message] }
    }
    if (response && !response.statusCode) {
      const status = response.paymentStatus ? response.paymentStatus.status : EPaymentStatus.DECLINED
      return { status: status === EPaymentStatus.DECLINED ? 'Failed' : 'OK', payment: response, message: [] }
    }
    return { status: 'Failed', message: Array.isArray(response.message) ? response.message : [response.message] }
  }

  @Action
  async autoPayNeedsReviewToggle (autoPayItemId: number | string) {
    const response = await Api.payments.autoPayNeedsReviewToggle(autoPayItemId)
    if (response.statusCode === 400) {
      return { status: 'Failed', message: [response.message] }
    }
    if (response === 'OK') {
      return { status: 'OK' }
    }
    return { status: 'Failed', message: Array.isArray(response.message) ? response.message : [response.message] }
  }

  @Action
  async scheduleItemChangePaymentMethod (input: { scheduleItem: ISchedulePaymentItem, paymentMethod: IResidentPaymentMethod }) {
    const scheduleSetting:ISchedulePaymentSetting = input.scheduleItem.setting as ISchedulePaymentSetting
    const response = await Api.payments.scheduleItemChangePaymentMethod(scheduleSetting.id, input.paymentMethod.id)
    if (response === 'OK') {
      this.UPDATE_SCHEDULE_SETTING_PAYMENT_METHOD({ scheduleSettingId: scheduleSetting.id, paymentMethod: input.paymentMethod })
    }
  }

  @Action
  async refundPayment (data: any) {
    const response: any = await Api.payments.refundPayment(data.paymentId, data.refundData)
    if (response && !response.statusCode) {
      return { status: 'OK', payment: response, message: [] }
    }
    return { status: 'Failed', message: Array.isArray(response.message) ? response.message : [response.message] }
  }

  @Action
  async fetchScheduleSettingStatistics (scheduleSettingId: number | string) {
    const statistics = await Api.payments.getScheduleSettingStatistics(scheduleSettingId)
    this.SET_SCHEDULE_SETTING_STATISTICS(statistics)
  }

  @Action
  async generateReceipt (paymentId: number | string) {
    const pdf = await Api.payments.generateReceipt(paymentId)
    return pdf
  }

  @Action
  async editPayment (data: any) {
    const response = await Api.payments.editPayment(data.paymentId, data.residentId, data.transactions)
    return response
  }

  @Action
  async loadDeposits (filters: any) {
    const response = await Api.payments.getDeposits(filters)
    for (let i = 0; i < response.length; i++) {
      if (response[i].id !== `Undeposited Funds`) {
        response[i].isFeeOnly = response[i].feesAmount ? -1 * response[i].feesAmount === response[i].amount : false
      }
    }
    this.SET_DEPOSITS(response)
  }

  @Action
  async loadPccDeposits (filters: IGetPccDepositListDto) {
    if (typeof filters !== 'object') {
      filters = this.filters
    }
    const response = await Api.payments.getPccDeposits(filters)
    this.SET_PCC_DEPOSITS(response)
  }

  @Action
  async resetPccDeposits () {
    this.RESET_PCC_DEPOSITS()
  }

  @Action
  async getUnassignedPccDepositsStatsByUsername () {
    return Api.payments.getUnassignedPccDepositsStatsByUsername()
  }

  @Action
  async assignPccDepositsByPccUsername (username: string) {
    return Api.payments.assignPccDepositsByPccUsername(username)
  }

  @Action
  async loadDepositPayments (filters: any) {
    const response = await Api.payments.getDepositsTransactions(filters)
    this.UPDATE_DEPOSIT_PAYMENTS(response.payments)
    this.UPDATE_DEPOSIT_PAYMENTS_TOTAL(response.total)
  }

  @Action
  async fetchFacilityPaymentsNotInSync (facilities: any[]) {
    const payments = await Api.statement.getFacilitiesPaymentsNotInSync(facilities)
    this.SET_FACILITIES_PAYMENTS_NOT_IN_SYNC(payments)
  }

  @Action
  async fetchBatchPaymentsNotInSync (batchId: number) {
    const payments = await Api.statement.getPaymentsNotSync(batchId)
    this.SET_FACILITIES_PAYMENTS_NOT_IN_SYNC(payments)
  }

  @Action
  async applyPaymentToPcc (input: { depositId: number, effectiveDate: Date | string}) {
    const response = await Api.payments.applyDepositToPcc(input.depositId, input.effectiveDate)
    if (response === 'OK') {
      this.UPDATE_DEPOSIT_STATUS({ depositId: input.depositId, status: EPaymentsDepositStatus.PROCESSING })
    }
  }

  @Action
  async applyPayment (input : { depositId: number, facilityOrigin: EOrigin }) {
    const response = await Api.payments.applyDeposit(input.depositId, input.facilityOrigin)
    if (response === 'OK') {
      this.UPDATE_DEPOSIT_STATUS({ depositId: input.depositId, status: EPaymentsDepositStatus.SYNCED })
    }
  }

  @Action
  async getBanks (filters: any) {
    const response = await Api.payments.getBanksList(filters)
    return response
  }

  @Action
  exportDeposits (filters: any) {
    Api.payments.exportDeposits(filters)
  }

  @Action
  exportDeposit (deposit: number) {
    Api.payments.exportDeposit(deposit)
  }

  @Action
  downloadDepositForExport (willowId: number) {
    Api.payments.downloadDepositForExport(willowId)
  }

  @Action
  downloadDepositReport (input: { depositId: number, fileName: string }) {
    return Api.payments.downloadDepositReport(input.depositId, input.fileName)
  }

  @Action
  assignUserToPccDeposits (depositIds: number[]) {
    return Api.payments.assignUserToPccDeposits(depositIds)
  }

  @Action
  async getPaymentPayerTypes (facilityId: number) {
    const res = await Api.payments.getPaymentPayerTypes(facilityId)
    this.SET_PAYMENT_PAYER_TYPES(res)
  }

  @Action
  getPreferredPayersForPayment (paymentId: number) {
    return Api.payments.getPreferredPayersForPayment(paymentId)
  }

  @Action
  async markPCCDepositAsSync (willowDepositId: number) {
    const response = await Api.payments.markPCCDepositAsSync(willowDepositId)
    return response
  }

  get getFilters () {
    return this.filters
  }
}
